import { createContext, useContext, useEffect, useState } from 'react';
import { Check, Lock, Moon, Palette, Sun } from 'lucide-react';
import { COMPANION_PORTRAIT } from './art';
import { Button, Popover } from './ui';
import { CompanionCloud } from './LVCompanion';
import { useAppearanceLock } from './appearanceGate';

export const THEMES = [
  { id: 'ember', label: 'Obsidian & Ember', animal: 'Dragon', art: 'dragon', message: 'Small steps. Stronger skills. Keep your spark alive.' },
  { id: 'forest', label: 'Deep Forest & Signal', animal: 'Fox', art: 'fox', message: 'Stay curious. Every question is a new trail forward.' },
  { id: 'gold', label: 'Noir & Gold', animal: 'Lion', art: 'lion', message: 'Confidence is earned, one skill at a time.' },
  { id: 'aurora', label: 'Midnight Aurora', animal: 'Owl', art: 'owl', message: 'Take a breath. Look again. You’re learning more than you think.' },
];

const ThemeContext = createContext({ palette: 'ember', mode: 'light', setPalette: () => {}, setMode: () => {} });
export const useTheme = () => useContext(ThemeContext);

const KEY = 'preppeak-appearance';

export function ThemeProvider({ children }) {
  const [palette, setPalette] = useState('ember');
  const [mode, setMode] = useState('light');
  const [ready, setReady] = useState(false);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (saved && THEMES.some((t) => t.id === saved.palette)) setPalette(saved.palette);
      if (saved && (saved.mode === 'light' || saved.mode === 'dark')) setMode(saved.mode);
    } catch { /* unavailable storage keeps the default appearance */ }
    setReady(true);
  }, []);
  useEffect(() => {
    if (!ready) return;
    try { localStorage.setItem(KEY, JSON.stringify({ palette, mode })); } catch { /* appearance still works without storage */ }
  }, [palette, mode, ready]);
  return <ThemeContext.Provider value={{ palette, mode, setPalette, setMode }}>{children}</ThemeContext.Provider>;
}

// The element that carries the design tokens. Everything the design styles lives inside one of these.
// The tokens sit on the outer element and the layout classes on the inner one, because every design class is scoped to
// descendants of .lv-root (so nothing leaks into the other pages of the site).
export function LVScope({ className = '', children, ...rest }) {
  const { palette, mode } = useTheme();
  return (
    <div className={`lv-root ${mode === 'dark' ? 'dark' : ''}`} data-palette={palette} data-mode={mode}>
      <div className={className} {...rest}>{children}</div>
    </div>
  );
}

export function ThemeSelector({ signedIn = false }) {
  const { palette, mode, setPalette, setMode } = useTheme();
  const { locked } = useAppearanceLock(signedIn);
  const mine = THEMES.find((t) => t.id === palette) || THEMES[0];
  if (locked) {
    return (
      <Popover label="Colors and companion" panelClassName="w-72 max-w-[calc(100vw-24px)] p-4 text-center"
        trigger={({ toggle, open }) => (
          <Button variant="outline" size="icon" aria-label="Colors and companion are set until you finish your assessment" aria-expanded={open} title="Set until you finish your assessment" className="shrink-0 rounded-full" onClick={toggle}><Lock /></Button>
        )}>
        <img src={COMPANION_PORTRAIT[mine.art]} alt="" loading="lazy" width={816} height={816} className="mx-auto h-24 w-24 object-contain" />
        <p className="mt-2 font-serif text-2xl">{mine.animal} is with you.</p>
        <p className="mt-2 text-sm text-muted-foreground">Your colors and companion stay the same until you finish your assessment. After that you can change them here.</p>
      </Popover>
    );
  }
  return (
    <Popover label="Colors and companion" panelClassName="w-80 max-w-[calc(100vw-24px)] p-4"
      trigger={({ toggle, open }) => (
        <Button variant="outline" size="icon" aria-label="Change colors and companion" aria-expanded={open} title="Colors & companion" className="shrink-0 rounded-full" onClick={toggle}><Palette /></Button>
      )}>
      <p className="font-serif text-2xl">Your colors. Your companion.</p>
      <div className="my-4 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
        <Button variant={mode === 'light' ? 'outline' : 'ghost'} onClick={() => setMode('light')} aria-pressed={mode === 'light'}><Sun /> Light</Button>
        <Button variant={mode === 'dark' ? 'outline' : 'ghost'} onClick={() => setMode('dark')} aria-pressed={mode === 'dark'}><Moon /> Dark</Button>
      </div>
      <div className="space-y-2">
        {THEMES.map((t) => (
          <Button key={t.id} variant="ghost" aria-pressed={palette === t.id} onClick={() => setPalette(t.id)}
            className={`h-auto w-full justify-start gap-3 whitespace-normal rounded-lg border px-2 py-2 text-left ${palette === t.id ? 'border-primary bg-secondary' : 'border-transparent'}`}>
            <img src={COMPANION_PORTRAIT[t.art]} alt="" loading="lazy" width={816} height={816} className="h-14 w-14 shrink-0 object-contain" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm">{t.label}</span>
              <span className="mt-1 flex items-center gap-2 text-xs text-muted-foreground"><span className={`palette-swatch swatch-${t.id}`} aria-hidden="true" />{t.animal}</span>
            </span>
            {palette === t.id && <Check className="text-primary" />}
          </Button>
        ))}
      </div>
    </Popover>
  );
}

export function CompanionBanner() {
  return <aside aria-label="Study companion" className="mb-8 border-b border-border pb-5"><CompanionCloud /></aside>;
}
